import React, { Fragment, useEffect, useState, useCallback } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureCtaButton,
    FeatureChain,
    FeatureExampleCard,
    FeatureDisclaimer,
    FeatureSection,
    FeatureDownloadCta,
    FeatureScreenshot
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './AiEstimatorMain.css';

function AiEstimatorMain(){
    const { t, toLocalizedPath, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.aiEstimator.${key}`);

    const [loader, setLoader] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [step1View, setStep1View] = useState('attached'); // 'attached' | 'empty'
    const [step3View, setStep3View] = useState('result'); // 'result' | 'list'
    const [lightboxImage, setLightboxImage] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    // Close lightbox on Escape key
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key === 'Escape' && lightboxImage) {
                setLightboxImage(null);
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImage]);

    const openLightbox = useCallback((imageObj) => {
        setLightboxImage(imageObj);
    }, []);

    const closeLightbox = useCallback(() => {
        setLightboxImage(null);
    }, []);

    if (!loader) {
        return <Loader />;
    }

    const workflowSteps = [
        {
            number: '01',
            tabTitle: p('step1Title'),
            title: p('step1Title'),
            description: p('step1Description'),
            highlights: [
                p('step1Highlight1'),
                p('step1Highlight2'),
                p('step1Highlight3')
            ],
            imageSrc: step1View === 'attached'
                ? `${process.env.PUBLIC_URL}/images/features/ai-estimator/vision-photos-attached.png`
                : `${process.env.PUBLIC_URL}/images/features/ai-estimator/vision-empty-upload.png`,
            imageAlt: step1View === 'attached' ? p('screenshotAttachedAlt') : p('screenshotEmptyAlt'),
            caption: p('step1Title'),
            hasToggle: true
        },
        {
            number: '02',
            tabTitle: p('step2TabTitle'),
            title: p('step2Title'),
            description: p('step2Description'),
            highlights: [
                p('step2Highlight1'),
                p('step2Highlight2'),
                p('step2Highlight3')
            ],
            imageSrc: `${process.env.PUBLIC_URL}/images/features/ai-estimator/vision-context-notes.png`,
            imageAlt: p('screenshotContextAlt'),
            caption: p('step2Title')
        },
        {
            number: '03',
            tabTitle: p('step3Title'),
            title: p('step3Title'),
            description: p('step3Description'),
            highlights: [
                p('step3Highlight1'),
                p('step3Highlight2'),
                p('step3Highlight3')
            ],
            imageSrc: step3View === 'result'
                ? `${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimate-result.png`
                : `${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimates-list.png`,
            imageAlt: step3View === 'result' ? p('screenshotResultAlt') : p('screenshotListAlt'),
            caption: p('step3Title'),
            hasStep3Toggle: true
        },
        {
            number: '04',
            tabTitle: p('step4Title'),
            title: p('step4Title'),
            description: p('step4Description'),
            highlights: [
                p('step4Highlight1'),
                p('step4Highlight2'),
                p('step4Highlight3')
            ],
            imageSrc: `${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimates-quote.png`,
            imageAlt: p('screenshotQuoteAlt'),
            caption: p('step4Title')
        }
    ];

    const currentStep = workflowSteps[activeStep];

    const featurePillars = [
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                </svg>
            ),
            title: p('pillar1Title'),
            description: p('pillar1Description')
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
            ),
            title: p('pillar2Title'),
            description: p('pillar2Description')
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
            ),
            title: p('pillar3Title'),
            description: p('pillar3Description')
        },
        {
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                </svg>
            ),
            title: p('pillar4Title'),
            description: p('pillar4Description')
        }
    ];

    const heroCtaContent = (
        <div>
            <FeatureHeroCta
                primaryLabel={t('shared.featureStartFree')}
                primaryTarget="feature-download"
                secondaryLabel={t('shared.featureSeeHowItWorks')}
                secondaryTarget="feature-content"
            />

            <div className="ai-hero-trust-row">
                <div className="ai-hero-trust-item">
                    <svg className="ai-hero-trust-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{p('trust1')}</span>
                </div>
                <div className="ai-hero-trust-item">
                    <svg className="ai-hero-trust-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{p('trust2')}</span>
                </div>
                <div className="ai-hero-trust-item">
                    <svg className="ai-hero-trust-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{p('trust3')}</span>
                </div>
            </div>
        </div>
    );

    const heroVisual = (
        <div className="ai-hero-split-image-wrapper">
            <div className="ai-hero-floating-badge ai-badge-top-right">
                <div className="ai-floating-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                    </svg>
                </div>
                <div>
                    <div>{p('photosAnalyzed')}</div>
                    <span className="ai-floating-text-sub">{p('billableItemsFound')}</span>
                </div>
            </div>

            <FeatureScreenshot
                src={`${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimate-result.png`}
                alt={p('screenshotResultAlt')}
                size="lg"
                tilt="none"
                onClick={() => openLightbox({
                    src: `${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimate-result.png`,
                    alt: p('screenshotResultAlt'),
                    caption: p('heroScreenshotCaption')
                })}
            />

            <div className="ai-hero-floating-badge ai-badge-bottom-left">
                <div className="ai-floating-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </div>
                <div>
                    <div>{p('estimatedAmount')}</div>
                    <span className="ai-floating-text-sub">{p('laborMaterialsItemized')}</span>
                </div>
            </div>
        </div>
    );

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.aiEstimator} locale={locale} />

            {/* Hero Section */}
            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                note={p('heroNote')}
                cta={heroCtaContent}
                image={heroVisual}
            />

            {/* Feature Pillars Grid */}
            <section id="feature-content" className="ai-pillars-section">
                <div className="container">
                    <div className="ai-pillars-grid">
                        {featurePillars.map((pillar, idx) => (
                            <div className="ai-pillar-card" key={idx}>
                                <div className="ai-pillar-icon-box">{pillar.icon}</div>
                                <h3 className="ai-pillar-title">{pillar.title}</h3>
                                <p className="ai-pillar-desc">{pillar.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive Workflow Section */}
            <div id="how-it-works">
                <FeatureSection
                    alt
                    title={p('sectionTitle')}
                    description={p('sectionDescription')}
                >
                    <FeatureChain steps={[p('chainStep1'), p('chainStep2'), p('chainStep3'), p('chainStep4')]} />

                    {/* Step Switcher Tabs */}
                    <div className="ai-workflow-tabs">
                        {workflowSteps.map((step, idx) => (
                            <button
                                key={idx}
                                className={`ai-workflow-tab${activeStep === idx ? ' active' : ''}`}
                                onClick={() => setActiveStep(idx)}
                                type="button"
                            >
                                <span className="ai-workflow-tab-num">{step.number}</span>
                                <span>{step.tabTitle}</span>
                            </button>
                        ))}
                    </div>

                    {/* Active Step Showcase Box */}
                    <div className="ai-showcase-box">
                        <div className="ai-showcase-row">
                            <div className="ai-showcase-content">
                                <span className="ai-showcase-badge">{p('stepProgress').replace('{{number}}', currentStep.number)}</span>
                                <h3 className="ai-showcase-title">{currentStep.title}</h3>
                                <p className="ai-showcase-description">{currentStep.description}</p>

                                {currentStep.hasToggle && (
                                    <div className="ai-toggle-group">
                                        <button
                                            type="button"
                                            className={`ai-toggle-btn${step1View === 'attached' ? ' active' : ''}`}
                                            onClick={() => setStep1View('attached')}
                                        >
                                            {p('photosAttached')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`ai-toggle-btn${step1View === 'empty' ? ' active' : ''}`}
                                            onClick={() => setStep1View('empty')}
                                        >
                                            {p('emptyUploadScreen')}
                                        </button>
                                    </div>
                                )}

                                {currentStep.hasStep3Toggle && (
                                    <div className="ai-toggle-group">
                                        <button
                                            type="button"
                                            className={`ai-toggle-btn${step3View === 'result' ? ' active' : ''}`}
                                            onClick={() => setStep3View('result')}
                                        >
                                            {p('estimateBreakdown')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`ai-toggle-btn${step3View === 'list' ? ' active' : ''}`}
                                            onClick={() => setStep3View('list')}
                                        >
                                            {p('savedEstimatesList')}
                                        </button>
                                    </div>
                                )}

                                <ul className="ai-checklist">
                                    {currentStep.highlights.map((h, i) => (
                                        <li key={i} className="ai-checklist-item">
                                            <svg className="ai-checklist-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>{h}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="ai-showcase-nav">
                                    <button
                                        type="button"
                                        className="ai-nav-btn"
                                        disabled={activeStep === 0}
                                        onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
                                    >
                                        ← {p('previous')}
                                    </button>
                                    <button
                                        type="button"
                                        className="ai-nav-btn ai-nav-btn-primary"
                                        disabled={activeStep === workflowSteps.length - 1}
                                        onClick={() => setActiveStep(prev => Math.min(workflowSteps.length - 1, prev + 1))}
                                    >
                                        {p('nextStep')} →
                                    </button>
                                </div>
                            </div>

                            <div
                                className="ai-showcase-image-wrapper"
                                onClick={() => openLightbox({
                                    src: currentStep.imageSrc,
                                    alt: currentStep.imageAlt,
                                    caption: `${currentStep.number}. ${currentStep.title}`
                                })}
                            >
                                <FeatureScreenshot
                                    src={currentStep.imageSrc}
                                    alt={currentStep.imageAlt}
                                    size="md"
                                    tilt="none"
                                />
                                <span className="ai-zoom-hint">🔍 {p('clickToZoom')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Step Sequence Overview Strip */}
                    <div className="ai-strip-section">
                        <h4 className="ai-strip-heading">{p('journeyHeading')}</h4>
                        <div className="ai-steps-grid">
                            {workflowSteps.map((step, idx) => (
                                <div
                                    key={idx}
                                    className="ai-step-card-item"
                                    onClick={() => openLightbox({
                                        src: step.imageSrc,
                                        alt: step.imageAlt,
                                        caption: `${step.number}. ${step.title}`
                                    })}
                                >
                                    <span className="ai-step-card-badge">{step.number}</span>
                                    <h5 className="ai-step-card-title">{step.title}</h5>
                                    <p className="ai-step-card-desc">{step.description}</p>
                                    <div className="ai-step-card-image-wrap">
                                        <FeatureScreenshot
                                            src={step.imageSrc}
                                            alt={step.imageAlt}
                                            size="sm"
                                            tilt="none"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </FeatureSection>
            </div>

            {/* Quote Conversion & Real-World Output Section */}
            <section className="ai-comparison-section">
                <div className="container">
                    <div className="ai-comparison-header">
                        <span className="ai-comparison-eyebrow">{p('comparisonEyebrow')}</span>
                        <h2 className="h-1 h-bold fc-section-title">{p('comparisonTitle')}</h2>
                        <p className="fc-section-description">
                            {p('comparisonDescription')}
                        </p>
                    </div>

                    <div className="ai-comparison-row">
                        <div className="ai-comparison-col">
                            <div className="ai-comparison-card-tag">
                                <span className="ai-tag-pill">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                                        <line x1="12" y1="18" x2="12.01" y2="18"></line>
                                    </svg>
                                    {p('mobileAiResult')}
                                </span>
                            </div>
                            <div
                                className="ai-showcase-image-wrapper"
                                onClick={() => openLightbox({
                                    src: `${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimate-result.png`,
                                    alt: p('screenshotResultAlt'),
                                    caption: p('estimateBreakdownCaption')
                                })}
                            >
                                <FeatureScreenshot
                                    src={`${process.env.PUBLIC_URL}/images/features/ai-estimator/ai-estimate-result.png`}
                                    alt={p('screenshotResultAlt')}
                                    size="md"
                                    tilt="none"
                                />
                                <span className="ai-zoom-hint">🔍 {p('clickToZoom')}</span>
                            </div>
                        </div>

                        <div className="ai-comparison-divider-col">
                            <div className="ai-arrow-badge" title={p('convertToQuote')}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </div>
                        </div>

                        <div className="ai-comparison-col">
                            <div className="ai-comparison-card-tag">
                                <span className="ai-tag-pill">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                    </svg>
                                    {p('customerFacingQuote')}
                                </span>
                            </div>
                            <FeatureExampleCard
                                title={p('exampleTitle')}
                                meta={p('exampleMeta')}
                                groups={[
                                    {
                                        sectionTitle: p('exampleWorkSectionTitle'),
                                        rows: [
                                            { label: p('exampleWork1'), value: '' },
                                            { label: p('exampleWork2'), value: '' },
                                            { label: p('exampleWork3'), value: '' },
                                            { label: p('exampleWork4'), value: '' }
                                        ]
                                    },
                                    {
                                        sectionTitle: p('exampleCostSectionTitle'),
                                        rows: [
                                            { label: p('exampleLaborLabel'), value: formatCurrency(p('exampleLaborValue')) },
                                            { label: p('exampleMaterialsLabel'), value: formatCurrency(p('exampleMaterialsValue')) }
                                        ]
                                    }
                                ]}
                                total={{ label: p('exampleTotalLabel'), value: formatCurrency(p('exampleTotalValue')) }}
                                cta={<FeatureCtaButton to={toLocalizedPath('quotes')}>{p('exampleCta')}</FeatureCtaButton>}
                            />
                        </div>
                    </div>

                    <FeatureDisclaimer>{p('disclaimer')}</FeatureDisclaimer>
                </div>
            </section>

            {/* Final Call to Action */}
            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            {/* Lightbox Zoom Modal */}
            {lightboxImage && (
                <div className="ai-lightbox-overlay" onClick={closeLightbox}>
                    <div className="ai-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="ai-lightbox-close"
                            onClick={closeLightbox}
                            aria-label={p('closeImagePreview')}
                        >
                            ✕
                        </button>
                        <div className="ai-lightbox-img-wrapper">
                            <img
                                src={lightboxImage.src}
                                alt={lightboxImage.alt}
                                className="ai-lightbox-img"
                            />
                        </div>
                        {lightboxImage.caption && (
                            <p className="ai-lightbox-caption">{lightboxImage.caption}</p>
                        )}
                    </div>
                </div>
            )}

            <Footer />
        </Fragment>
    );
}

export default AiEstimatorMain;